import { LiquidityPool, LiquidityPosition, Vault } from 'nftx.js';
import { IncreaseLiquidityDetails, Step } from './types';
import { useMemo, useReducer, useState } from 'react';
import { formatEther, parseEther } from 'viem';
import useUserAssets from '@/application/read/assets/useUserAssets';
import useAddress from '@/application/read/web3/useAddress';
import useBalances from '@/application/read/tokens/useBalances';
import { useBalance } from 'wagmi';
import IncreaseLiquidity from './IncreaseLiquidity';
import getWeakestStatus from '@/presentation/utils/getWeakestStatus';
import {
  calculatePriceFromTick,
  isAddressToken0,
} from '@/domain/ticks/helpers';
import usePoolCurrentTick from '@/application/read/pools/usePoolCurrentTick';
import { useNetwork } from '@nftx/react';

const reducer = (
  state: IncreaseLiquidityDetails,
  action: Partial<IncreaseLiquidityDetails>
) => ({
  ...state,
  ...action,
});

const IncreaseLiquidityContainer = ({
  position,
  pool,
  vault,
  withinModal,
  icon,
}: {
  position: LiquidityPosition;
  pool: LiquidityPool;
  vault: Vault;
  withinModal: boolean;
  icon?: string;
}) => {
  const network = useNetwork();
  const poolType = useMemo(() => {
    if (position.tickLower > pool.tick) {
      return 'nft';
    }
    if (position.tickUpper < pool.tick) {
      return 'eth';
    }
    return 'pair';
  }, [pool.tick, position.tickLower, position.tickUpper]);

  const collection = vault.asset.id;
  const collectionName = vault.asset.name;
  const isVtoken0 = isAddressToken0(collection, network);

  const { data: currentTick = 0n } = usePoolCurrentTick(pool.id);
  const currentPrice = calculatePriceFromTick(currentTick, isVtoken0);

  const [state, dispatch] = useReducer(reducer, {
    ethAmount: '0',
    fullRange: false,
    items: [],
    liquidityAmount: '0',
    maxPercent: 0,
    minPercent: 0,
    maxPriceInEth: formatEther(position.tickUpperValue),
    minPriceInEth: formatEther(position.tickLowerValue),
    price: formatEther(currentPrice),
    tick: `${pool.tick}`,
    tickLower: Number(position.tickLower),
    tickUpper: Number(position.tickUpper),
    tokenAmount: '0',
    using: 'nft',
  } as IncreaseLiquidityDetails);

  const steps = useMemo(() => {
    const steps: Step[] = [];

    if (poolType === 'nft' || poolType === 'pair') {
      steps.push('using');
      if (state.using === 'nft') {
        steps.push('inventory');
      }
    }

    steps.push('range', 'processing', 'success');

    return steps;
  }, [poolType, state.using]);
  const [step, setStep] = useState<Step>(() => steps[0]);

  const userAddress = useAddress();

  const { data: assets, status: assetsStatus } = useUserAssets({
    userAddress,
    assetAddress: collection,
    vaultId: position.vaultId,
    mintable: true,
  });

  const { data: ethBalance } = useBalance({ address: userAddress });
  const { data: balances, status: balancesStatus } = useBalances({
    userAddress,
  });

  const amountMax = useMemo(() => {
    if (
      (poolType === 'pair' || poolType === 'nft') &&
      state.using === 'vtoken'
    ) {
      return (
        balances?.find((b) => b.vaultId === position.vaultId)?.balance ?? 0n
      );
    }
    const totalItems = state.items.reduce((total, item) => {
      const q = Number(item.quantity ?? 1n);
      return total + q;
    }, 0);
    return parseEther(`${totalItems}`);
  }, [balances, poolType, position.vaultId, state.items, state.using]);

  const status = getWeakestStatus(assetsStatus, balancesStatus);

  return (
    <IncreaseLiquidity
      amountMax={amountMax}
      collection={collection}
      collectionName={collectionName}
      currentApy={pool.apr['1m']}
      currentNFTPriceInETH={currentPrice}
      currentTick={currentTick}
      dispatch={dispatch}
      ethMax={ethBalance?.value ?? 0n}
      feeTier={pool.feeTier.toString()}
      icon={icon}
      is1155={vault.is1155}
      liquidity={pool.totalLiquidity}
      poolAddress={pool.id}
      poolType={poolType}
      setStep={setStep}
      state={state}
      step={step}
      steps={steps}
      symbol={vault.token.symbol}
      tokenAddress={vault.id}
      userAssets={assets}
      vaultId={position.vaultId}
      withinModal={withinModal}
      position={position}
      userAddress={userAddress}
      status={status}
    />
  );
};

export default IncreaseLiquidityContainer;
